import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TRANSACTIONS } from "@/constants/testIds";

const EXPENSE_CATS = [
  { v: "tax", l: "Impuestos" },
  { v: "utility", l: "Servicios" },
  { v: "expenses", l: "Expensas" },
  { v: "insurance", l: "Seguro" },
  { v: "broker", l: "Comisión" },
  { v: "accountant", l: "Contador" },
  { v: "maintenance", l: "Mantenimiento" },
  { v: "other", l: "Otro" },
];

const INCOME_CATS = [
  { v: "rent", l: "Alquiler" },
  { v: "deposit", l: "Depósito" },
  { v: "other", l: "Otro" },
];

const empty = () => ({
  property_id: "",
  kind: "income",
  category: "rent",
  amount: "",
  currency: "USD",
  date: new Date().toISOString().slice(0, 10),
  description: "",
});

export default function TransactionDialog({ open, onOpenChange, properties, defaultPropertyId, onSaved }) {
  const [form, setForm] = useState(empty());

  useEffect(() => {
    if (open) {
      const f = empty();
      if (defaultPropertyId) {
        f.property_id = defaultPropertyId;
        const p = properties.find((x) => x.id === defaultPropertyId);
        if (p) f.currency = p.currency;
      } else if (properties.length > 0) {
        f.property_id = properties[0].id;
        f.currency = properties[0].currency;
      }
      setForm(f);
    }
  }, [open, defaultPropertyId, properties]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", {
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success("Movimiento registrado");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const cats = form.kind === "income" ? INCOME_CATS : EXPENSE_CATS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={TRANSACTIONS.dialog} className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nuevo movimiento</DialogTitle>
          <DialogDescription>El monto se convierte automáticamente a USD al momento de registrar.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Propiedad</Label>
            <Select value={form.property_id} onValueChange={(v) => {
              set("property_id", v);
              const p = properties.find((x) => x.id === v);
              if (p) set("currency", p.currency);
            }}>
              <SelectTrigger data-testid="tx-form-property"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {p.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => {
              set("kind", v);
              set("category", v === "income" ? "rent" : "tax");
            }}>
              <SelectTrigger data-testid="tx-form-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Ingreso</SelectItem>
                <SelectItem value="expense">Gasto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Categoría</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Monto</Label>
            <Input type="number" step="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="tx-form-amount" />
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="MXN">MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Fecha</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="tx-form-submit">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
